import { useState, useEffect } from "react";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Gamepad2, Trophy, Wind, Zap, Grid3x3, Palette } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import BreathingPacer from "../components/games/BreathingPacer";
import ReactionTimer from "../components/games/ReactionTimer";
import MemoryGrid from "../components/games/MemoryGrid";
import ColorWordStroop from "../components/games/ColorWordStroop";
import Leaderboard from "../components/games/Leaderboard";
import GameStats from "../components/games/GameStats";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERACTIVE_GAMES = [
  {
    id: "breathing-pacer",
    title: "Breathing Pacer",
    description: "Follow the animated circle to regulate your breathing rhythm. Trains parasympathetic activation.",
    game_type: "breathing-rhythm",
    difficulty: "beginner",
    duration_minutes: 3,
    icon: Wind,
    gradient: "from-blue-500 to-cyan-500",
    component: BreathingPacer,
  },
  {
    id: "reaction-timer",
    title: "Reaction Timer",
    description: "Test your reaction speed across 10 rounds. Builds focus and reflexes under pressure.",
    game_type: "focus-challenge",
    difficulty: "intermediate",
    duration_minutes: 2,
    icon: Zap,
    gradient: "from-purple-500 to-pink-500",
    component: ReactionTimer,
  },
  {
    id: "memory-grid",
    title: "Memory Grid",
    description: "Watch the pattern flash, then repeat it from memory. Trains working memory and concentration.",
    game_type: "focus-challenge",
    difficulty: "intermediate",
    duration_minutes: 5,
    icon: Grid3x3,
    gradient: "from-amber-500 to-orange-500",
    component: MemoryGrid,
  },
  {
    id: "color-word-stroop",
    title: "Color Word Challenge",
    description: "Name the ink color, not the word. The classic Stroop test for mental agility and impulse control.",
    game_type: "focus-challenge",
    difficulty: "advanced",
    duration_minutes: 1,
    icon: Palette,
    gradient: "from-indigo-500 to-blue-500",
    component: ColorWordStroop,
  },
];

export default function MindfulnessGames({ user, onLogout }) {
  const [guidedGames, setGuidedGames] = useState([]);
  const [activeGame, setActiveGame] = useState(null);
  const [selectedGuided, setSelectedGuided] = useState(null);
  const [showComplete, setShowComplete] = useState(false);
  const [sessionNotes, setSessionNotes] = useState("");
  const [sessions, setSessions] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetchGuidedGames();
    fetchSessions();
  }, []);

  const fetchGuidedGames = async () => {
    try {
      const res = await axios.get(`${API}/mindfulness-games`);
      // Deduplicate by title
      const unique = [];
      const seen = new Set();
      for (const g of res.data) { if (!seen.has(g.title)) { seen.add(g.title); unique.push(g); } }
      setGuidedGames(unique);
    } catch { toast.error("Failed to load games"); }
  };

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/mindfulness-games/sessions`, { headers: { Authorization: `Bearer ${token}` } });
      setSessions(res.data);
    } catch (e) { console.error(e); }
  };

  const handleInteractiveComplete = async (gameId, gameTitle, gameType, score, duration) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/mindfulness-games/sessions`, {
        game_id: gameId, game_title: gameTitle, game_type: gameType,
        score, duration_played: Math.round(duration), notes: `Score: ${score}`
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Score saved: ${score} points!`);
      setActiveGame(null);
      fetchSessions();
      setRefreshKey(k => k + 1);
    } catch { toast.error("Failed to save score"); }
  };

  const handleGuidedComplete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/mindfulness-games/sessions`, {
        game_id: selectedGuided.id, game_title: selectedGuided.title,
        game_type: selectedGuided.game_type, notes: sessionNotes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Session completed!");
      setShowComplete(false);
      setSelectedGuided(null);
      setSessionNotes("");
      fetchSessions();
      setRefreshKey(k => k + 1);
    } catch { toast.error("Failed to log session"); }
  };

  const getDifficultyColor = (d) => ({
    beginner: "bg-green-100 text-green-800",
    intermediate: "bg-amber-100 text-amber-800",
    advanced: "bg-red-100 text-red-800"
  }[d] || "bg-gray-100 text-gray-800");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/20">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 data-testid="games-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">
            Mindfulness Games
          </h1>
          <p className="text-sm text-gray-500 mt-1">Interactive exercises to sharpen focus, build resilience, and train mental agility</p>
        </div>

        {/* Game Stats */}
        <div className="mb-8" key={`stats-${refreshKey}`}>
          <GameStats />
        </div>

        {/* Interactive Games */}
        <h2 className="text-xl font-bold text-gray-900 mb-4">Interactive Games</h2>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {INTERACTIVE_GAMES.map(game => {
            const Icon = game.icon;
            return (
              <Card
                key={game.id}
                data-testid={`game-card-${game.id}`}
                className="border-0 shadow-lg bg-white hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer group"
                onClick={() => setActiveGame(game)}
              >
                <div className={`h-24 bg-gradient-to-br ${game.gradient} rounded-t-lg flex items-center justify-center`}>
                  <Icon className="w-12 h-12 text-white opacity-90 group-hover:scale-110 transition-transform" />
                </div>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getDifficultyColor(game.difficulty)}`}>
                      {game.difficulty}
                    </span>
                    <span className="text-[10px] text-gray-400">{game.duration_minutes} min</span>
                  </div>
                  <CardTitle className="text-base">{game.title}</CardTitle>
                  <CardDescription className="text-xs">{game.description}</CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        {/* Leaderboard + Guided Games */}
        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          <div className="lg:col-span-1" key={`lb-${refreshKey}`}>
            <Leaderboard />
          </div>
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Guided Practices</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {guidedGames.map(game => (
                <Card
                  key={game.id}
                  data-testid={`guided-card-${game.id}`}
                  className="border-0 shadow-md bg-white hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => setSelectedGuided(game)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getDifficultyColor(game.difficulty)}`}>{game.difficulty}</span>
                      <span className="text-[10px] text-gray-400">{game.duration_minutes} min</span>
                    </div>
                    <CardTitle className="text-sm">{game.title}</CardTitle>
                    <CardDescription className="text-xs">{game.description}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Sessions */}
        <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Sessions</h2>
        <div className="space-y-2 mb-8">
          {sessions.slice(0, 8).map(session => (
            <div key={session.id} data-testid={`session-${session.id}`} className="flex items-center justify-between bg-white rounded-lg shadow-sm px-4 py-3 border border-gray-100">
              <div className="flex items-center gap-3">
                <Trophy className="w-4 h-4 text-amber-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">{session.game_title}</p>
                  <p className="text-[10px] text-gray-400">{new Date(session.completed_at).toLocaleDateString()}</p>
                </div>
              </div>
              {session.score > 0 && <span className="text-sm font-bold text-indigo-600">{session.score} pts</span>}
            </div>
          ))}
          {sessions.length === 0 && <p className="text-center text-gray-400 py-6 text-sm">No sessions yet. Play your first game!</p>}
        </div>
      </div>

      {/* Interactive Game Dialog */}
      <Dialog open={!!activeGame} onOpenChange={() => setActiveGame(null)}>
        <DialogContent data-testid="interactive-game-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">{activeGame?.title}</DialogTitle>
            <DialogDescription className="text-sm">{activeGame?.description}</DialogDescription>
          </DialogHeader>
          {activeGame && (
            <activeGame.component
              onComplete={(score, duration) =>
                handleInteractiveComplete(activeGame.id, activeGame.title, activeGame.game_type, score, duration)
              }
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Guided Game Instructions Dialog */}
      <Dialog open={!!selectedGuided && !showComplete} onOpenChange={() => setSelectedGuided(null)}>
        <DialogContent data-testid="guided-game-dialog" className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedGuided?.title}</DialogTitle>
            <DialogDescription>{selectedGuided?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <h3 className="font-semibold mb-2">How to Practice:</h3>
            <ol className="space-y-2">
              {selectedGuided?.instructions.map((inst, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                  <span className="text-sm text-gray-700">{inst}</span>
                </li>
              ))}
            </ol>
            <div className="bg-indigo-50 p-3 rounded-lg text-xs text-indigo-800">
              <strong>Duration:</strong> {selectedGuided?.duration_minutes} min. Find a quiet space.
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowComplete(true)} className="flex-1 bg-indigo-600 hover:bg-indigo-700">Begin Practice</Button>
              <Button onClick={() => setSelectedGuided(null)} variant="outline" className="flex-1">Maybe Later</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Guided Session Dialog */}
      <Dialog open={showComplete} onOpenChange={setShowComplete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>How was your practice?</DialogTitle>
            <DialogDescription>Reflect on your experience</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea placeholder="What did you notice? Any insights?" value={sessionNotes} onChange={(e) => setSessionNotes(e.target.value)} rows={4} />
            <Button onClick={handleGuidedComplete} className="w-full bg-indigo-600 hover:bg-indigo-700">Complete Session</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
